import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineError } from "@/components/ui/inline-error";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Loader2, Lock, CheckCircle } from "lucide-react";

const passwordSchema = z.object({
  password: z.string().min(6, { message: "Le mot de passe doit contenir au moins 6 caractères" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  // Vérifier qu'on a bien un token de reset dans l'URL
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get("access_token");
    const type = hashParams.get("type");

    if (!accessToken || type !== "recovery") {
      toast({
        title: "Lien invalide",
        description: "Ce lien de réinitialisation n'est pas valide ou a expiré.",
        variant: "destructive",
      });
      navigate("/auth");
    }
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const validation = passwordSchema.safeParse(formData);
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.issues.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: formData.password,
    });

    setLoading(false);

    if (error) {
      let errorMessage = error.message;
      if (error.message.includes("same as")) {
        errorMessage = "Le nouveau mot de passe doit être différent de l'ancien.";
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      setSuccess(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  // Écran de succès
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Card */}
        <div className="relative z-10 w-full max-w-md animate-fade-up">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
              <div className="text-center space-y-4 mb-6">
                <div className="mx-auto p-4 rounded-full bg-green-500/10 w-fit">
                  <CheckCircle className="h-10 w-10 text-green-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Mot de passe modifié !</h1>
                <p className="text-white/50">
                  Votre mot de passe a été mis à jour avec succès.
                </p>
              </div>

              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full h-12 font-semibold bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F]"
              >
                Accéder au tableau de bord
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
      {/* Gradient mesh background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
          <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
            {/* Header */}
            <div className="text-center space-y-4 mb-6">
              <div className="mx-auto p-4 rounded-full bg-[#F59E0B]/10 w-fit">
                <Lock className="h-10 w-10 text-[#F59E0B]" />
              </div>
              <h1 className="text-2xl font-bold text-white">Nouveau mot de passe</h1>
              <p className="text-white/50">
                Choisissez un nouveau mot de passe pour votre compte.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-white/70">
                  Nouveau mot de passe
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.password ? "border-red-500" : ""}`}
                />
                {errors.password && (
                  <InlineError message={errors.password} className="mt-1" />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-white/70">
                  Confirmer le mot de passe
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.confirmPassword ? "border-red-500" : ""}`}
                />
                {errors.confirmPassword && (
                  <InlineError message={errors.confirmPassword} className="mt-1" />
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base rounded-xl"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Changer le mot de passe
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
